import { Injectable } from '@angular/core';
import { OfferViewModel } from '../models/offer-view.model';

interface OfferDetailView {
  companyId: string;
  offerId: string;
  savedAt: number;
  offer: OfferViewModel;
}

/**
 * Traspaso de la oferta entre la pestaña que lista las ofertas y la pestaña nueva que muestra su detalle.
 * La pestaña nueva no comparte memoria con la original, por eso la oferta viaja por localStorage
 * (compartido entre pestañas del mismo origen). Solo se guarda la última oferta consultada.

 */
@Injectable({ providedIn: 'root' })
export class OfferDetailViewService {
  private readonly storageKey = 'offerDetailView';
  // Pasado este tiempo el traspaso se ignora y la pestaña consulta la oferta de nuevo.
  private readonly maxAgeMs = 15 * 60 * 1000;

  save(offer: OfferViewModel): void {
    const payload: OfferDetailView = {
      companyId: String(offer.company?.id ?? ''),
      offerId: String(offer.offerId),
      savedAt: Date.now(),
      // preselecta y urlRedirect pueden traer el token `lead` del aliado y la vista de detalle no los usa:
      // no se persisten.
      offer: { ...offer, preselecta: {}, urlRedirect: '' },
    };

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(payload));
    } catch {
      // Storage bloqueado o lleno: la pestaña nueva no encontrará el traspaso y usará la landing de la oferta.
    }
  }

  read(companyId: string, offerId: string): OfferViewModel | null {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) {
        return null;
      }

      const payload = JSON.parse(raw) as OfferDetailView;
      const isFresh = Date.now() - payload.savedAt <= this.maxAgeMs;
      const isSameOffer = payload.companyId === companyId && payload.offerId === offerId;

      return isFresh && isSameOffer ? payload.offer : null;
    } catch {
      return null;
    }
  }
}
